import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'
import { Axios } from 'utils'
import { updateCompany, updateProfile } from './userSlice'
import { fetchUserProfile } from '../../services'
import { useDispatch } from 'react-redux'

interface User {
  id: string
  email: string
  name: string
  img?: string
  password?: string
  token?: string
}

interface AuthState {
  user: User | null
  loading: boolean
  error: string | null
  passwordExpired: boolean
  otpToken: string | null
}

const initialState: AuthState = {
  user: null,
  loading: false,
  error: null,
  passwordExpired: false,
  otpToken: null,
}

export const loginUser = createAsyncThunk(
  'auth/loginUser',
  async (
    payload: Pick<User, 'email' | 'password'>,
    { dispatch, rejectWithValue }
  ) => {
    try {
      const { token, profile, ...rest } = (
        await Axios.post('auth/login', payload, {
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        })
      ).data
      dispatch(updateProfile({ ...profile, token }))
      dispatch(updateCompany(profile?.company))

      // Check if user needs to accept terms of use
      const requiresTOU =
        rest.current_active_tou_version_id &&
        (!rest.tou_version_id ||
          rest.tou_version_id !== rest?.current_active_tou_version_id)

      return { profile, requiresTOU: !!requiresTOU }
    } catch (error: any) {
      if (error?.otp_token) {
        return rejectWithValue({
          type: 'PASSWORD_EXPIRED',
          message: error.detail,
          otp_token: error.otp_token,
        })
      }

      const errorMessage = error?.detail || 'Incorrect username or password'
      return rejectWithValue({ type: 'NORMAL_ERROR', message: errorMessage })
    }
  }
)

export const withMe = createAsyncThunk(
  'auth/withMe',
  async (_, { dispatch, rejectWithValue }) => {
    try {
      const { profile } = await fetchUserProfile()
      dispatch(updateProfile({ ...profile }))
      dispatch(updateCompany(profile?.company))
      return profile
    } catch (error: any) {
      localStorage.removeItem('token')
      return rejectWithValue(
        error?.response?.data?.detail || 'Authentication failed'
      )
    }
  }
)

export const useRefreshUser = () => {
  const dispatch = useDispatch()
  return () => dispatch(withMe())
}

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    logout(state) {
      state.user = null
      localStorage.removeItem('token')
    },
    resetAuthError(state) {
      state.error = null
      state.passwordExpired = false
      state.otpToken = null
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loginUser.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(
        loginUser.fulfilled,
        (
          state,
          action: PayloadAction<{ profile: any; requiresTOU: boolean }>
        ) => {
          state.loading = false
        }
      )
      .addCase(loginUser.rejected, (state, action) => {
        state.loading = false
        const payload = action.payload as any
        if (payload?.type === 'PASSWORD_EXPIRED') {
          state.passwordExpired = true
          state.otpToken = payload.otp_token
          state.error = payload.message
        } else {
          state.passwordExpired = false
          state.otpToken = null
          state.error = payload?.message || 'Incorrect username or password'
        }
      })
      .addCase(withMe.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(withMe.fulfilled, (state) => {
        state.loading = false
      })
      .addCase(withMe.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload as string
      })
  },
})

export const { logout, resetAuthError } = authSlice.actions
export default authSlice.reducer
